<template>
  <esg-table-without-meta :items="items"
                          :fields="fields"
                          max-height="300px"
                          size="sm">
    <template #thead-top>
      <tr>
        <th colspan="4"><span>법인 정보</span></th>
        <th colspan="4"><span>제품 정보</span></th>
        <th colspan="4"><span>수송 원재료 정보</span></th>
        <th colspan="2"><span>협력사 정보</span></th>
        <th colspan="4"><span>수송 정보</span></th>
        <th colspan="4"><span>배출량 정보</span></th>
      </tr>
    </template>
  </esg-table-without-meta>
</template>

<script>
import EsgTableWithoutMeta from "@/components/esg/base/EsgTableWithoutMeta.vue";

export default {
  components: { EsgTableWithoutMeta },
  // 업스트림 배출 상세 테이블
  props: {
    year: {
      type: Number
    },
    product: {
      type: Object
    }
  },
  watch: {
    product: {
      deep: true,
      immediate: true,
      handler() {
        this.fetchData()
      }
    },
    year: {
      immediate: true,
      handler() {
        this.fetchData()
      }
    }
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    async fetchData() {
      if (!this.product || !this.product.productFamilyCode || !this.product.productCode || !this.product.name) return;

      try {
        // Chart meta data
        let args = [{
          years: [this.year],
          productFamilies: [this.product.productFamilyCode],
          products: [this.product.productCode],
        }];

        let data = new FormData()
        data.append('path', 'esg/lca/upstream_transportation_detail/main');
        data.append('json', JSON.stringify(args));

        this.abortController = new AbortController();
        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        // If there is no data, return
                if (!response.data.data) {
          this.series = [];
          return;
        }


        // Parse JSON data
        let chartData = JSON.parse(response.data.data)[0].data;

        // Filter out the data in the past
        let items = []

        let prevIdx = -1
        let currIdx = -1
        let currHeader = null
        let total = 0
        let totalPerProduction = 0
        chartData.forEach(row => {
          // Current index is the production ID of the row
          currIdx = row.production_id

          // When a new group starts
          if (currIdx !== prevIdx) {
            // Create a new row and push it to the new array
            let newRow = Object.assign({}, row)
            newRow.input_id = '-'
            newRow.input_name = '-'
            newRow.input_amount = '-'
            newRow.input_unit = '-'
            newRow.departure = '-'
            newRow.destination = '-'
            newRow.transportation_mode = '-'
            newRow.partner_code = '-'
            newRow.partner_name = '-'
            newRow.distance = '-'
            newRow.carbon_emission_per_transport_amount = '-';
            newRow.carbon_emission_per_input_amount = '-';
            newRow.carbon_emission_amount = 0
            newRow.carbon_emission_per_production_amount = 0

            // Push it to the new array
            items.push(newRow)
            prevIdx = currIdx

            // Add GHG emissions to totals and change header
            if (currHeader) {
              total += currHeader.carbon_emission_amount
              totalPerProduction += currHeader.carbon_emission_per_production_amount
            }
            currHeader = newRow
          }

          // Push the current row
          row.product_amount = '-'
          row.product_unit = '-'
          currHeader.carbon_emission_amount += row.carbon_emission_amount
          currHeader.carbon_emission_per_production_amount += row.carbon_emission_per_production_amount
          items.push(row)
        })

        // For the last header, add GHG emissions to totals
        if (currHeader) {
          total += currHeader.carbon_emission_amount
          totalPerProduction += currHeader.carbon_emission_per_production_amount
        }

        this.items = items
        this.$emit('totalCalculated', total, totalPerProduction)
      } catch (error) {
        await this.showError(error)
      }
    },
  },
  data() {
    return {
      currYear: new Date().getFullYear(),
      items: [],
      fields: [
        {
          name: "corporation_code",
          label: "법인 코드",
          list: true,
          sortable: true
        },
        {
          name: "corporation_name",
          label: "법인",
          list: true,
          filtered: true,
        },
        {
          name: "business_location_code",
          label: "사업장 코드",
          list: true,
        },
        {
          name: "business_location_name",
          label: "사업장",
          list: true,
          filtered: true,
        },
        {
          name: "product_family_code",
          label: "제품군 코드",
          list: true,
        },
        {
          name: "product_family_name",
          label: "제품군",
          list: true,
        },
        {
          name: "product_code",
          label: "제품 코드",
          list: true,
        },
        {
          name: "product_name",
          label: "제품명",
          list: true,
        },
        {
          name: "input_id",
          label: "원료 코드",
          list: true,
        },
        {
          name: "input_name",
          label: "원료명",
          list: true,
          filtered: true,
        },
        {
          name: "input_amount",
          label: "수송량",
          list: true,
        },
        {
          name: "input_unit",
          label: "수송 단위",
          list: true,
        },
        {
          name: "partner_code",
          label: "관리 코드",
          list: true,
        },
        {
          name: "partner_name",
          label: "업체명",
          list: true,
          filtered: true,
        },
        {
          name: "departure",
          label: "출발지",
          list: true,
          filtered: true,
        },
        {
          name: "destination",
          label: "수령지",
          list: true,
          filtered: true,
        },
        {
          name: "transportation_mode",
          label: "수송수단",
          list: true,
          filtered: true,
        },
        {
          name: "distance",
          label: "거리",
          list: true,
          type: 'Number',
          formatter: (val, item) => val === '-' ? val : `${val} (${item.distance_unit})`
        },
        {
          name: "carbon_emission_per_transport_amount",
          label: "수송 단위당 배출량",
          list: true,
          type: 'Number',
          formatter: (val, item) => val === '-' ? val : `${this.formatNumber(val)} (${item.carbon_emission_per_transport_unit})`
        },
        {
          name: "carbon_emission_per_input_amount",
          label: "원료 단위당 배출량",
          list: true,
          type: 'Number',
          formatter: (val, item) => val === '-' ? val : `${this.formatNumber(val)} (${item.carbon_emission_per_input_unit})`
        },
        {
          name: "carbon_emission_per_production_amount",
          label: "단위 제품당 배출량",
          list: true,
          type: 'Number',
          formatter: (val, item) => `${this.formatNumber(val)} (${item.carbon_emission_per_production_unit})`
        },
        {
          name: "carbon_emission_amount",
          label: '온실가스 배출량\n(tCO2e)',
          list: true,
          type: 'Number',
          formatter: val => this.formatNumber(val)
        },
      ]
    };
  },
};
</script>
